import { Box, IconButton, Typography } from '@mui/material';
import MarketProLogo from '../../javascript/images/MarketProLogo.png'
import "../../stylesheets/chat_page.css"
import { useState, useEffect, useLayoutEffect } from 'react';
import { SignInApple, SignInGoogle, SignInUpEmail } from './NewAccountPage';
import { isMobileBrowser } from '../utils/user_agent';
import { isDarkMode } from '../utils/constants';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { trackFrontendEvent } from '../utils/tracking';
import { pendingSecondQuestionEventMetadata } from '../utils/signup_modal';
import { trackConversionGateEvent } from '../utils/conversionGateTracking';

const SignUpModalV2 = ({
  userData,
  setUserData,
  setShowSignUpModal,
  sidebarOffset = 0,
  desktopAnchorId = null,
}) => {
  const [opacity, setOpacity] = useState(0)
  const [desktopAnchorCenter, setDesktopAnchorCenter] = useState(null)
  const handleClose = () => {
    const metadata = pendingSecondQuestionEventMetadata();
    trackFrontendEvent("signup_modal_dismissed", metadata);
    trackConversionGateEvent("signup_modal_dismissed", metadata);
    setShowSignUpModal(false);
  };

  const [errors, setErrors] = useState([])
  const setErrorsArray = (error) => {
    if (!error) return setErrors([]);
    if (Array.isArray(error)) return setErrors(error.filter(Boolean));
    if (typeof error === 'string') return setErrors([error]);
    if (error.message) return setErrors([error.message]);
    return setErrors([String(error)]);
  };

  useEffect(() => {
    setOpacity(1.0)
    const metadata = pendingSecondQuestionEventMetadata();
    trackFrontendEvent("signup_modal_viewed", metadata);
    trackConversionGateEvent("signup_modal_viewed", metadata);
  }, [])

  const isMobile = isMobileBrowser()

  useLayoutEffect(() => {
    if (isMobile || !desktopAnchorId || typeof document === 'undefined') {
      setDesktopAnchorCenter(null)
      return undefined
    }

    const anchor = Array.from(
      document.querySelectorAll('[data-workspace-module-id]'),
    ).find(
      (element) =>
        element.getAttribute('data-workspace-module-id') ===
        String(desktopAnchorId),
    )
    if (!anchor) {
      setDesktopAnchorCenter(null)
      return undefined
    }

    const updateAnchorCenter = () => {
      const rect = anchor.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight
      const visibleLeft = Math.max(0, rect.left)
      const visibleRight = Math.min(viewportWidth, rect.right)
      const visibleTop = Math.max(0, rect.top)
      const visibleBottom = Math.min(viewportHeight, rect.bottom)
      if (visibleRight <= visibleLeft || visibleBottom <= visibleTop) {
        setDesktopAnchorCenter(null)
        return
      }
      setDesktopAnchorCenter({
        left: (visibleLeft + visibleRight) / 2,
        top: (visibleTop + visibleBottom) / 2,
        width: visibleRight - visibleLeft,
      })
    }

    updateAnchorCenter()
    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(updateAnchorCenter)
    resizeObserver?.observe(anchor)
    window.addEventListener('resize', updateAnchorCenter)

    return () => {
      resizeObserver?.disconnect()
      window.removeEventListener('resize', updateAnchorCenter)
    }
  }, [desktopAnchorId, isMobile])

  const trackProviderClick = (provider) => {
    const metadata = pendingSecondQuestionEventMetadata({ provider });
    trackFrontendEvent("signup_provider_clicked", metadata);
    trackConversionGateEvent("signup_provider_clicked", metadata);
  };

  return (
    <div className="modal modal-paywall" data-signup-modal data-signup-modal-anchor={desktopAnchorId || undefined} role="dialog" aria-modal="true" aria-label="Create a free Incite account" style={{
      opacity: opacity,
      transition: 'opacity 0.35s ease',
      backgroundColor: isDarkMode ? 'rgba(2, 6, 14, 0.72)' : 'rgba(10, 24, 48, 0.32)',
      zIndex: 1600,
      }}>
      <div className="paywall-modal-content" style={{
        left:
          !isMobile && desktopAnchorCenter
            ? `${desktopAnchorCenter.left}px`
            : undefined,
        top:
          !isMobile && desktopAnchorCenter
            ? `${desktopAnchorCenter.top}px`
            : undefined,
        marginLeft:
          !isMobile && !desktopAnchorCenter
            ? `${sidebarOffset/2}px`
            : 'unset',
        boxSizing:
          !isMobile && desktopAnchorCenter ? 'border-box' : undefined,
        width: '462px',
        maxWidth:
          !isMobile && desktopAnchorCenter
            ? `${Math.max(0, desktopAnchorCenter.width - 32)}px`
            : 'calc(100% - 32px)',
        maxHeight: 'calc(100vh - 32px)',
        padding: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        borderRadius: '24px',
        border: isDarkMode ? '1px solid rgba(62,84,118,0.55)' : '1px solid rgba(163,189,233,0.55)',
        background: isDarkMode
          ? 'linear-gradient(180deg, #131A28 0%, #0D111B 100%)'
          : 'linear-gradient(180deg, #FFFFFF 0%, #F4F8FF 100%)',
        boxShadow: isDarkMode
          ? '0 30px 90px rgba(0,0,0,0.56)'
          : '0 30px 90px rgba(31, 88, 174, 0.24)',
      }}>
        <Box
          sx={{
            p: { xs: 2.25, sm: 3 },
            pr: { xs: 5.5, sm: 6 },
            position: 'relative',
            textAlign: 'left',
            borderBottom: `1px solid ${isDarkMode ? 'rgba(68,85,112,0.45)' : 'rgba(187,208,243,0.65)'}`,
            background: isDarkMode
              ? 'radial-gradient(120% 120% at 100% 0%, rgba(39,129,255,0.3) 0%, rgba(17,27,43,0) 62%)'
              : 'radial-gradient(130% 130% at 100% 0%, rgba(47,127,255,0.2) 0%, rgba(255,255,255,0) 62%)',
          }}
        >
          <IconButton
            onClick={handleClose}
            aria-label="Close sign up dialog"
            sx={{
              position: 'absolute',
              top: 12,
              right: 12,
              color: isDarkMode ? '#D5E4FF' : '#23529A',
            }}
          >
            <CloseRoundedIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src={MarketProLogo}
              className="logo-img"
              alt="Incite AI"
              sx={{ width: 30, height: 30, flexShrink: 0 }}
            />
          </Box>

          <Typography
            sx={{
              mt: 1.45,
              fontSize: { xs: '21px', sm: '26px' },
              lineHeight: 1.08,
              fontWeight: 750,
              color: isDarkMode ? '#F3F8FF' : '#0F294F',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            Keep the conversation going
          </Typography>
          <Typography
            sx={{
              mt: 0.45,
              fontSize: { xs: '18px', sm: '22px' },
              lineHeight: 1.08,
              fontWeight: 740,
              color: isDarkMode ? '#7DB1FF' : '#176DDE',
              fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
            }}
          >
            Continue for free
          </Typography>
          <Typography
            sx={{
              mt: 1.5,
              fontSize: '14px',
              lineHeight: 1.6,
              color: isDarkMode ? '#A5BEDF' : '#335B8F',
              maxWidth: 430,
            }}
          >
            Create an account to save this thread and keep asking questions with Incite&rsquo;s market context.
          </Typography>
          <Typography
            sx={{
              mt: 1.15,
              fontSize: '13px',
              lineHeight: 1.45,
              fontWeight: 700,
              color: isDarkMode ? '#D5E4FF' : '#23529A',
            }}
          >
            No card required.
          </Typography>
        </Box>

        <Box sx={{
          px: { xs: 2.25, sm: 3 },
          pt: { xs: 0.5, sm: 0.75 },
          pb: { xs: 2.25, sm: 3 },
          textAlign: 'left',
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 0.25,
          }}>
            {errors.length > 0 && (
              <Box sx={{ width: '100%', color: 'rgba(220,38,38,0.82)', textAlign: 'left' }}>
                <ul>{errors.map((error) => { return <li key={error}>{error}</li> })}</ul>
              </Box>
            )}
            <SignInGoogle
              overrideDarkMode={true}
              userData={userData}
              setUserData={setUserData}
              setErrors={setErrorsArray}
              setShowSignUpModal={setShowSignUpModal}
              onProviderClick={trackProviderClick}
              trackingMetadata={pendingSecondQuestionEventMetadata()}
            />
            <SignInApple
              overrideDarkMode={true}
              userData={userData}
              setUserData={setUserData}
              setErrors={setErrorsArray}
              setShowSignUpModal={setShowSignUpModal}
              onProviderClick={trackProviderClick}
              trackingMetadata={pendingSecondQuestionEventMetadata()}
            />
            <SignInUpEmail
              overrideDarkMode={true}
              userData={userData}
              setUserData={setUserData}
              setErrors={setErrorsArray}
              setShowSignUpModal={setShowSignUpModal}
              onProviderClick={trackProviderClick}
            />
            <Typography
              sx={{
                mt: 2.75,
                fontSize: '13px',
                lineHeight: 1.45,
                fontWeight: 700,
                color: isDarkMode ? '#A5BEDF' : '#335B8F',
                textAlign: 'center',
              }}
            >
              Used by 300K+ investors
            </Typography>
          </Box>
        </Box>
      </div>
    </div>
  )
}

export default SignUpModalV2
